import { useEffect, useState } from "react";
import { Menu, X, Languages } from "lucide-react";
import { scrollToId } from "./shared";
import { useLang } from "@/i18n";

const links = [
  { key: "story", href: "#story" },
  { key: "oils", href: "#oils" },
  { key: "process", href: "#process" },
  { key: "quality", href: "#quality" },
  { key: "community", href: "#community" },
  { key: "films", href: "#films" },
  { key: "gallery", href: "#gallery" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const langToggle = (
    <button
      onClick={() => setLang(lang === "en" ? "hi" : "en")}
      data-testid="lang-toggle-button"
      className="flex items-center gap-1.5 rounded-full border border-ink/25 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-cream"
      aria-label="Switch language"
    >
      <Languages className="h-3.5 w-3.5" />
      {lang === "en" ? "हिंदी" : "EN"}
    </button>
  );

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-ink/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 lg:px-12">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3"
          data-testid="nav-logo-link"
        >
          <img src="/assets/logo.jpeg" alt="Arniveda logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-ink/10" />
          <span className="font-serif text-lg font-semibold tracking-tight">Arniveda</span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.key}`}
              className="text-xs font-medium uppercase tracking-[0.18em] text-moss transition-colors duration-300 hover:text-terracotta"
            >
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {langToggle}
          <button
            onClick={(e) => go(e, "#enquiry")}
            data-testid="nav-enquire-button"
            className="hidden rounded-full bg-olive px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-ink sm:block"
          >
            {t("nav.enquire")}
          </button>
          <button
            className="lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-ink/10 bg-cream px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                className="font-serif text-2xl font-medium"
              >
                {t(`nav.${l.key}`)}
              </a>
            ))}
            <button
              onClick={(e) => go(e, "#enquiry")}
              className="mt-2 rounded-full bg-olive px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-cream"
              data-testid="nav-mobile-enquire-button"
            >
              {t("nav.enquire")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
